import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Scale, TriangleAlert, Undo2, Users } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { cn } from '../../lib/cn'
import { post } from '../../lib/api'
import { useStaffStream } from '../../lib/useStaffStream'

/**
 * The panel's tie-break — slide 22's third and last tie-break.
 *
 * Two entries can finish level on the final score *and* on the three
 * highest-weighted KPIs. At that point the deck says "decision of the judging
 * panel", and nothing a computer does can substitute for it. So the three
 * judges are asked directly: each puts the tied entries in order, and the
 * entry most of them place first takes the place.
 *
 * **Why this is not a dialog the judge can dismiss.** It appears over the
 * console because the ceremony is blocked until it is answered — Admin cannot
 * publish the podium, and the compère is waiting. A tie-break that could be
 * closed and forgotten would be found at the worst possible moment.
 *
 * **Why the other judges' ballots are shown.** This is the one thing in the
 * judge portal that is deliberately *not* private. Slide 18 keeps every
 * judge's marks to themselves, and that still holds — but a panel decision is
 * made together, so each judge sees who has voted and which way. Their marks
 * are never in this payload.
 */

interface TieEntry {
  subjectKey: string
  label: string
  detail: string | null
  finalScore: number
}

export interface TieGroup {
  rubricCode: string
  rubricTitle: string
  awardTitle: string
  place: number
  entries: TieEntry[]
  groupKey: string
  /** What the projector prints per entry once decided; empty until then. */
  screenScores: Record<string, number>
  votes: { judgeId: number; judgeLabel: string; ordering: string[] | null }[]
  resolved: string[] | null
}

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'] as const
const ordinal = (n: number) => ORDINALS[n - 1] ?? `${n}th`

/** "Judge 1, Judge 2 and Judge 3" — not "Judge 1 and Judge 2 and Judge 3". */
function listOf(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`
}

/**
 * Subscribes to the ties the panel has to settle.
 *
 * Pushed rather than polled, like everything else on the day: a ballot from
 * another judge, or Admin re-opening a sheet and the tie dissolving, both
 * arrive without a refresh.
 */
export function useTieStream(enabled: boolean) {
  return useStaffStream<{ ties: TieGroup[] }>({
    ticketPath: '/api/judge/stream-ticket',
    streamPath: '/api/judge/stream',
    eventName: 'ties',
    enabled,
  })
}

/**
 * One tie, as a ballot.
 *
 * The judge clicks the entries in the order they rank them — first click is
 * 1st place, next is 2nd. A plain ordered pick rather than drag-and-drop:
 * this is used once or twice a year, on a laptop, under time pressure, and
 * "click your winner" needs no explaining.
 */
function Ballot({
  tie,
  judgeId,
  onDone,
}: {
  tie: TieGroup
  judgeId: number
  onDone: () => void
}) {
  const mine = tie.votes.find((v) => v.judgeId === judgeId)

  /** The ballot being built, as subject keys in the order clicked. */
  const [picked, setPicked] = useState<string[]>([])

  /*
   * Reset whenever the tie itself changes — a different group of entries, or
   * a mark correction that rebuilt it. A half-finished ballot from the
   * previous tie must not be submittable against this one.
   */
  useEffect(() => {
    setPicked(mine?.ordering ?? [])
  }, [tie.groupKey, tie.place, mine?.ordering?.join('|')])

  const submit = useMutation({
    mutationFn: (ordering: string[]) =>
      post<{ status: string; resolved: string[] | null }>(
        `/api/judge/ties/${tie.rubricCode}/${tie.place}`,
        { ordering },
        'staff',
      ),
    onSuccess: onDone,
  })

  const complete = picked.length === tie.entries.length
  const changed = (mine?.ordering ?? []).join('|') !== picked.join('|')

  const waiting = tie.votes.filter((v) => v.ordering == null)
  const score = tie.entries[0]?.finalScore

  /** What the next tap will assign, or null once the ballot is full. */
  const nextPlace = complete ? null : tie.place + picked.length

  return (
    <div>
      {/*
        One sentence of explanation, not three.

        The first version said the same thing in the title, in the dialog
        description *and* in an amber box: "1st is tied", "the scores cannot
        separate these entries", "2 entries are level on 58.50… they are also
        level on the three highest-weighted criteria". A judge reading that
        under time pressure has to work out that it is one fact, not three.
      */}
      <p className="text-[13px] leading-relaxed text-slate-600">
        Both scored <b className="font-semibold text-[#0B1E3F]">{score?.toFixed(2)}</b> and are
        level on the three highest-weighted criteria, so the marks cannot separate them. Your
        order decides {ordinal(tie.place)}
        {tie.entries.length === 2 ? ` and ${ordinal(tie.place + 1)}` : ' onwards'}.
      </p>

      <p className="mt-3.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">
        {complete ? 'Your order' : `Tap your choice for ${ordinal(nextPlace!)}`}
      </p>

      <ul className="mt-1.5 space-y-1.5">
        {tie.entries.map((entry) => {
          const at = picked.indexOf(entry.subjectKey)
          const chosen = at !== -1

          return (
            <li key={entry.subjectKey}>
              <button
                type="button"
                disabled={chosen || submit.isPending}
                onClick={() =>
                  setPicked((p) => {
                    const next = [...p, entry.subjectKey]
                    /*
                     * The last entry is implied, so fill it in. With two tied
                     * entries — far and away the common case — naming the
                     * winner settles the ballot in one tap, instead of making
                     * the judge also confirm the one that is left over.
                     */
                    const left = tie.entries.filter((e) => !next.includes(e.subjectKey))
                    return left.length === 1 ? [...next, left[0].subjectKey] : next
                  })
                }
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
                  chosen
                    ? 'border-[#1B2A6B]/30 bg-[#1B2A6B]/[0.04]'
                    : 'border-slate-200 bg-white hover:border-[#1B2A6B] hover:bg-slate-50',
                )}
              >
                <span
                  className={cn(
                    'grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-bold',
                    chosen
                      ? 'bg-[#1B2A6B] text-white'
                      : 'border border-dashed border-slate-300 text-[9px] font-semibold text-slate-400',
                  )}
                >
                  {chosen ? ordinal(tie.place + at) : 'tap'}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-bold leading-tight text-[#0B1E3F]">
                    {entry.label}
                  </span>
                  {entry.detail && (
                    <span className="block truncate text-[11px] leading-tight text-slate-500">
                      {entry.detail}
                    </span>
                  )}
                </span>

                {/*
                  No score on the row. It is the same number on every entry
                  by definition — printing it twice added noise and invited
                  the judge to look for a difference that is not there. It is
                  stated once, above.
                */}
              </button>
            </li>
          )
        })}
      </ul>

      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          disabled={!complete || submit.isPending || (!changed && mine?.ordering != null)}
          onClick={() => submit.mutate(picked)}
          className={cn(
            'h-9 flex-1 rounded-lg text-[13px] font-semibold text-white',
            'bg-[#1B2A6B] hover:bg-[#141F55]',
            'disabled:bg-slate-200 disabled:text-slate-400',
          )}
        >
          {submit.isPending
            ? 'Recording…'
            : mine?.ordering
              ? 'Change my decision'
              : 'Submit my decision'}
        </Button>

        {picked.length > 0 && (
          <Button
            type="button"
            variant="outline"
            disabled={submit.isPending}
            onClick={() => setPicked([])}
            className="h-9 rounded-lg border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
          >
            <Undo2 className="mr-1 size-3.5" /> Reset
          </Button>
        )}
      </div>

      {submit.isError && (
        <p className="mt-2 flex items-start gap-1.5 text-[11.5px] font-semibold text-red-600">
          <TriangleAlert className="mt-px size-3.5 shrink-0" />
          Could not record that — the tie may have changed. It will reload by itself.
        </p>
      )}

      {/* ---------- What the rest of the panel has done ---------- */}
      <div className="mt-3.5 border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">
            <Users className="size-3" /> The panel
          </p>
          <p className="text-[11px] tabular-nums text-slate-400">
            {tie.votes.length - waiting.length} of {tie.votes.length} decided
          </p>
        </div>

        {/* One row, not a stacked list — three judges fit across easily and
            the dialog was twice as tall as it needed to be. */}
        <ul className="mt-2 grid grid-cols-3 gap-1.5">
          {tie.votes.map((vote) => {
            const label = tie.entries.find((e) => e.subjectKey === vote.ordering?.[0])?.label
            return (
              <li
                key={vote.judgeId}
                className={cn(
                  'rounded-md px-2 py-1.5 text-center',
                  vote.ordering ? 'bg-emerald-50' : 'bg-slate-100/70',
                )}
              >
                <p className="truncate text-[10.5px] font-semibold text-slate-500">
                  {vote.judgeId === judgeId ? 'You' : vote.judgeLabel}
                </p>
                {vote.ordering ? (
                  <p className="mt-0.5 flex items-center justify-center gap-1 text-[11.5px] font-bold text-emerald-700">
                    <Check className="size-3 shrink-0" strokeWidth={3} />
                    <span className="truncate">{label}</span>
                  </p>
                ) : (
                  <p className="mt-0.5 text-[11.5px] text-slate-400">waiting</p>
                )}
              </li>
            )
          })}
        </ul>

        {tie.resolved ? (
          /*
           * The outcome, with the numbers the hall will actually see.
           *
           * The two entries scored the same, so the screen shows the lower
           * place a hundredth of a point down — otherwise the podium would
           * print one number against two different places, which reads as a
           * fault. The panel should know that is happening, and see it here,
           * rather than find out from the projector.
           */
          <div className="mt-2.5 rounded-lg bg-emerald-50 px-2.5 py-2">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700">
              Decided — this is what the screen will show
            </p>
            <ul className="mt-1.5 space-y-1">
              {tie.resolved.map((subjectKey, i) => (
                <li
                  key={subjectKey}
                  className="flex items-baseline justify-between gap-2 text-[12px]"
                >
                  <span className="truncate font-bold text-[#0B1E3F]">
                    <span className="mr-1.5 text-emerald-700">{ordinal(tie.place + i)}</span>
                    {tie.entries.find((e) => e.subjectKey === subjectKey)?.label}
                  </span>
                  <span className="shrink-0 font-mono font-bold tabular-nums text-emerald-700">
                    {(tie.screenScores[subjectKey] ?? 0).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[10.5px] leading-snug text-emerald-800/70">
              Your real marks are unchanged at{' '}
              {tie.entries[0]?.finalScore.toFixed(2)} — the screen separates them by 0.01 so
              one number is not printed against two places.
            </p>
          </div>
        ) : (
          <p className="mt-2.5 text-[11.5px] leading-snug text-slate-500">
            {waiting.length === 0
              ? 'The panel is split evenly — confer and change a decision. Two entries cannot share a place.'
              : `Waiting on ${listOf(waiting.map((v) => v.judgeLabel))}. Every judge votes before anything is decided.`}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Shows the ties waiting on this judge, one at a time.
 *
 * Deliberately modal and not dismissable: see the note at the top of the
 * file. It closes itself the moment the last tie is resolved.
 */
export function TieBreakPanel({ judgeId, ties }: { judgeId: number; ties: TieGroup[] }) {
  /*
   * Shown only while this judge actually has something to do.
   *
   *   - not voted yet            → blocking, they are holding the ceremony up
   *   - voted, waiting on others → gone; their part is done
   *   - everyone voted, still level → back, because a deadlock can only be
   *     broken by somebody changing their mind
   *
   * The first version kept it open until the tie *resolved*, which meant a
   * judge who had already decided sat behind a modal they could not dismiss
   * while they waited for the other two. Their console was unusable for no
   * reason.
   */
  const open = useMemo(
    () =>
      ties.filter((tie) => {
        if (tie.resolved != null) return false

        const mine = tie.votes.find((v) => v.judgeId === judgeId)
        const everyoneVoted = tie.votes.every((v) => v.ordering != null)

        return mine?.ordering == null || everyoneVoted
      }),
    [ties, judgeId],
  )
  const current = open[0]

  if (!current) return null

  return (
    <Dialog open>
      <DialogContent
        /*
         * Narrow on purpose. The first version ran to `max-w-lg` with
         * generous padding everywhere and filled most of a laptop screen for
         * what is, in substance, two names and a choice between them.
         *
         * No X, no outside-click, no Escape: the ceremony is blocked on this.
         */
        className="max-w-96"
        showClose={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="p-5">
          <p className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber-700">
            <Scale className="size-3.5" strokeWidth={2.4} />
            Panel decision
            {open.length > 1 && (
              <span className="font-semibold text-slate-400">· {open.length} to settle</span>
            )}
          </p>

          <DialogTitle className="mt-1 text-[17px] font-bold leading-tight text-[#0B1E3F]">
            {current.awardTitle}
          </DialogTitle>

          {/* The description carries the one fact the title does not: which
              place is contested. It is not a second copy of the explanation. */}
          <DialogDescription className="mt-0.5 text-[13px] font-semibold text-amber-700">
            {ordinal(current.place)} place is tied
          </DialogDescription>

          <div className="mt-3">
            <Ballot
              key={`${current.rubricCode}:${current.place}:${current.groupKey}`}
              tie={current}
              judgeId={judgeId}
              onDone={() => {
                /* The stream pushes the new state; nothing to do here. */
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
